import { _js } from '@ifixit/utils/localize';
import initializeComponent from 'Shared/react-initialize';
import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import YouTube from 'react-youtube';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForwardStep } from '@fortawesome/pro-duotone-svg-icons/faForwardStep';
import { faBackwardStep } from '@fortawesome/pro-duotone-svg-icons/faBackwardStep';
import { faRepeat } from '@fortawesome/pro-duotone-svg-icons/faRepeat';

type VideoPlayerProps = {
   youTubeVideoId?: string;
   videoId?: string;
   videoUrls?: {
      mp4_720?: string;
      mp4_592?: string;
      webm?: string;
      ogg?: string;
   };
   videoWidth?: number;
   videoHeight?: number;
   timestamps: [string, VideoTimestamp][];
};

type VideoTimestamp = {
   startTime: number;
   endTime: number;
};

type StepVideoTimestamp = {
   [stepNumber: number]: VideoTimestamp;
};

export default function VideoPlayer({
   youTubeVideoId,
   videoId,
   videoUrls,
   videoWidth,
   videoHeight,
   timestamps = [],
}: VideoPlayerProps) {
   const [currentTime, setCurrentTime] = useState<number>(0);
   const stepTimestamps: StepVideoTimestamp = stepNumberTimestamps(timestamps);
   const validStepNumbers: number[] = Object.keys(stepTimestamps)
      .map(step => Number.parseInt(step))
      .sort((a, b) => a - b);

   const youTubePlayerRef = useRef<YouTube | null>(null);
   const videoRef = useRef<HTMLVideoElement | null>(null);
   const isYouTube = !!youTubeVideoId;
   const isIFixitVideo = !!videoId && !!videoUrls;
   const isVerticalVideo = videoWidth && videoHeight && videoHeight > videoWidth;

   const fragmentStepNumber = getStepNumberFromStepid(window.location.hash.slice(2));
   const [stepNumber, setStepNumber] = useState<number | null>(
      fragmentStepNumber && validStepNumbers.includes(fragmentStepNumber)
         ? fragmentStepNumber
         : null
   );
   const [loopCurrentStep, setLoopCurrentStep] = useState<boolean>(stepNumber ? true : false);
   const [scrollTextWithVideo, setScrollTextWithVideo] = useState<boolean>(true);
   const [playerHeight, setPlayerHeight] = useState<number>(0);

   const handleScrollToStepClick = (event: any) => {
      const button = event.target;
      const stepNumber = Number.parseInt(button.dataset.stepNumber);
      if (validStepNumbers.includes(stepNumber)) {
         setScrollTextWithVideo(false);
         setStepNumber(stepNumber);
         scrollToStep(stepNumber, playerHeight);
         seekTo(stepTimestamps[stepNumber].startTime);
      }
   };

   const divRef = useRef<HTMLDivElement>(null);
   const lastHighlightedStep = useRef<number | null>(null);
   const currentStepRef = useRef<number | null>(stepNumber);

   const updateCenterTop = () => {
      if (!divRef.current || !divRef.current.offsetParent) return;
      const height = divRef.current.clientHeight;
      setPlayerHeight(height);

      const parentElement = divRef.current.offsetParent as HTMLElement;
      parentElement.style.setProperty('--video-player-half-height', `${height / 2}px`);
   };

   const seekTo = (time: number) => {
      if (isYouTube && youTubePlayerRef.current) {
         const player = youTubePlayerRef.current.internalPlayer;
         if (player && player.seekTo) {
            player.seekTo(time, true);
         }
      } else if (isIFixitVideo && videoRef.current) {
         const video = videoRef.current;
         if (video && video.readyState >= 1) {
            video.currentTime = time;
         } else {
            const handleLoadedData = () => {
               video.currentTime = time;
               video.removeEventListener('loadeddata', handleLoadedData);
            };
            video.addEventListener('loadeddata', handleLoadedData);
         }
      }
   };

   const getCurrentTime = async (): Promise<number> => {
      if (isYouTube && youTubePlayerRef.current) {
         const player = youTubePlayerRef.current.internalPlayer;
         if (player && player.getCurrentTime) {
            return await player.getCurrentTime();
         }
      } else if (isIFixitVideo && videoRef.current) {
         const video = videoRef.current;
         if (video && !isNaN(video.currentTime)) {
            return video.currentTime;
         }
      }
      return 0;
   };

   useEffect(() => {
      currentStepRef.current = stepNumber;
      if (lastHighlightedStep.current !== stepNumber) {
         highlightCurrentPlayingStep(validStepNumbers, stepNumber);
         lastHighlightedStep.current = stepNumber;
      }
   }, [stepNumber, validStepNumbers]);

   useEffect(() => {
      updateCenterTop();
      window.addEventListener('resize', updateCenterTop);

      let intervalId: ReturnType<typeof setInterval> | undefined;
      let isVideoScroll = false;

      const skipToStepButtons = Array.from(document.querySelectorAll('.skip-to-step'));
      skipToStepButtons.forEach(button => {
         button.addEventListener('click', handleScrollToStepClick);
      });

      let stepUpdateTimeout: ReturnType<typeof setTimeout> | undefined;

      const handleScroll = () => {
         if (isVideoScroll || validStepNumbers.length === 0) return;

         clearTimeout(stepUpdateTimeout);
         stepUpdateTimeout = setTimeout(() => {
            const videoHeight = playerHeight || 200;
            let currentVisibleStep: number | null = null;
            let minDistance = Infinity;

            validStepNumbers.forEach(stepNum => {
               const stepElement = document.querySelector(
                  `li.step[data-step-number="${CSS.escape(String(stepNum))}"]`
               );
               if (stepElement) {
                  const stepRect = stepElement.getBoundingClientRect();
                  const targetPosition = videoHeight / 2;
                  const distance = Math.abs(stepRect.top - targetPosition);

                  if (
                     stepRect.top > 0 &&
                     stepRect.top < window.innerHeight &&
                     distance < minDistance
                  ) {
                     minDistance = distance;
                     currentVisibleStep = stepNum;
                  }
               }
            });

            if (
               currentVisibleStep &&
               stepTimestamps[currentVisibleStep] &&
               currentVisibleStep !== currentStepRef.current &&
               minDistance < 200 &&
               !scrollTextWithVideo &&
               !loopCurrentStep
            ) {
               setStepNumber(currentVisibleStep);
               const targetTime = stepTimestamps[currentVisibleStep].startTime;
               setTimeout(() => {
                  seekTo(targetTime);
               }, 100);
            }
         }, 750);
      };

      window.addEventListener('scroll', handleScroll, { passive: true });

      if (loopCurrentStep) {
         if (currentStepRef.current === null) {
            isVideoScroll = true;
            scrollToStep(validStepNumbers[0], playerHeight);
            setStepNumber(validStepNumbers[0]);
            setTimeout(() => {
               isVideoScroll = false;
            }, 1000);
         } else {
            const startTime = stepTimestamps[currentStepRef.current]?.startTime;
            const endTime = stepTimestamps[currentStepRef.current]?.endTime;
            intervalId = setInterval(async () => {
               const time = await getCurrentTime();
               if (time >= endTime || time < startTime) {
                  setCurrentTime(time);
                  seekTo(startTime);
               }
            }, 500);
         }
         if (scrollTextWithVideo && currentStepRef.current) {
            isVideoScroll = true;
            scrollToStep(currentStepRef.current, playerHeight);
            setTimeout(() => {
               isVideoScroll = false;
            }, 1000);
         }
      } else if (scrollTextWithVideo) {
         intervalId = setInterval(async () => {
            const time = await getCurrentTime();
            setCurrentTime(time);
            let found = false;
            let currentStepNumber: number | null = null;

            timestamps.forEach(([stepid, timestamp]: [string, VideoTimestamp]) => {
               if (time >= timestamp.startTime && time < timestamp.endTime) {
                  currentStepNumber = getStepNumberFromStepid(stepid);
                  found = true;
               }
            });

            if (!found) {
               currentStepNumber = null;
            }

            if (currentStepNumber !== null && currentStepNumber !== currentStepRef.current) {
               setStepNumber(currentStepNumber);
               isVideoScroll = true;
               scrollToStep(currentStepNumber, playerHeight);
               setTimeout(() => {
                  isVideoScroll = false;
               }, 1000);
            } else if (currentStepNumber === null && currentStepRef.current !== null) {
               setStepNumber(null);
            }
         }, 500);
      } else {
         intervalId = trackTimestamps(getCurrentTime, setStepNumber, setCurrentTime, timestamps);
      }

      return () => {
         clearInterval(intervalId);
         clearTimeout(stepUpdateTimeout);
         skipToStepButtons.forEach(button => {
            button.removeEventListener('click', handleScrollToStepClick);
         });
         window.removeEventListener('resize', updateCenterTop);
         window.removeEventListener('scroll', handleScroll);
      };
   }, [loopCurrentStep, scrollTextWithVideo, playerHeight]);

   const handleUserVideoTimeUpdate = () => {
      if (videoRef.current) {
         setCurrentTime(videoRef.current.currentTime);
      }
   };

   const renderVideoPlayer = () => {
      if (isYouTube) {
         return (
            <YouTube
               videoId={youTubeVideoId}
               opts={{
                  width: '100%',
                  height: '100%',
               }}
               className={'floating-video-player-iframe'}
               ref={youTubePlayerRef}
            />
         );
      } else if (isIFixitVideo) {
         const getBestVideoSource = () => {
            if (videoUrls.mp4_720) return videoUrls.mp4_720;
            if (videoUrls.mp4_592) return videoUrls.mp4_592;
            if (videoUrls.webm) return videoUrls.webm;
            if (videoUrls.ogg) return videoUrls.ogg;
            return null;
         };

         const videoSource = getBestVideoSource();
         if (!videoSource) {
            return <div>Video not available</div>;
         }

         return (
            <video
               ref={videoRef}
               className="floating-video-player-iframe"
               controls
               width="100%"
               height="100%"
               onTimeUpdate={handleUserVideoTimeUpdate}
            >
               {videoUrls.mp4_720 && <source src={videoUrls.mp4_720} type="video/mp4" />}
               {videoUrls.mp4_592 && <source src={videoUrls.mp4_592} type="video/mp4" />}
               {videoUrls.webm && <source src={videoUrls.webm} type="video/webm" />}
               {videoUrls.ogg && <source src={videoUrls.ogg} type="video/ogg" />}
               <span>Your browser does not support the video tag.</span>
            </video>
         );
      }
      return <div>No video source provided</div>;
   };

   // Calculate aspect ratio for the video player
   const getAspectRatio = () => {
      if (videoWidth && videoHeight) {
         return `${videoWidth} / ${videoHeight}`;
      }
      return '16 / 9'; // Default for YouTube and videos without dimensions
   };

   const playerClassName = isVerticalVideo
      ? 'floating-video-player floating-video-player-vertical'
      : 'floating-video-player';

   return (
      <div className={playerClassName} ref={divRef} style={{ aspectRatio: getAspectRatio() }}>
         {renderVideoPlayer()}
         <div className="floating-video-player-bottom-bar flex justify-between items-center p-2 bg-gray-200">
            <p
               onClick={() => {
                  if (stepNumber) {
                     scrollToStep(stepNumber, playerHeight);
                  }
               }}
               className="m-0 text-brand-500 cursor-pointer font-semibold"
            >
               {stepNumber ? <span>Step {stepNumber}</span> : <span></span>}
            </p>
            <div className="flex justify-center items-center gap-1">
               <button
                  type="button"
                  className={`bottom-bar-component scroll-with-video-button inline-flex items-center justify-center h-8 px-3 rounded-md border border-gray-300 text-sm font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${scrollTextWithVideo ? 'bg-brand-200' : 'bg-white'}`}
                  onClick={() => {
                     setScrollTextWithVideo(!scrollTextWithVideo);
                  }}
                  title="Scroll the guide with video playback"
               >
                  <span>{_js('Auto-Scroll')}</span>
               </button>
               <button
                  type="button"
                  className={`bottom-bar-component loop-current-step-button inline-flex items-center justify-center h-8 px-3 rounded-md border border-gray-300 text-sm font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${loopCurrentStep ? 'bg-brand-200' : 'bg-white'}`}
                  onClick={() => {
                     if (!loopCurrentStep && stepNumber) {
                        scrollToStep(stepNumber, playerHeight);
                     }
                     setLoopCurrentStep(!loopCurrentStep);
                  }}
                  title="Loop the section of the video for the current step"
               >
                  <FontAwesomeIcon icon={faRepeat} />
               </button>
               <button
                  type="button"
                  className={`bottom-bar-component previous-step-button inline-flex items-center justify-center h-8 px-3 rounded-md border border-gray-300 text-sm font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 bg-white`}
                  disabled={stepTimestamps[validStepNumbers[0]]?.startTime > currentTime}
                  onClick={() => {
                     const prevStep: number =
                        stepNumber === null
                           ? getNearestPreviousStep(currentTime, stepTimestamps)
                           : getPreviousTimestampedStep(stepNumber, validStepNumbers);
                     setStepNumber(prevStep);
                     scrollToStep(prevStep, playerHeight);
                     if (!loopCurrentStep) {
                        seekTo(stepTimestamps[prevStep].startTime);
                     }
                  }}
                  title="Previous Step"
               >
                  <FontAwesomeIcon icon={faBackwardStep} />
               </button>
               <button
                  type="button"
                  className={`bottom-bar-component next-step-button inline-flex items-center justify-center h-8 px-3 rounded-md border border-gray-300 text-sm font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 bg-white`}
                  disabled={
                     validStepNumbers.length === 0 ||
                     (stepNumber !== null &&
                        stepNumber >= (validStepNumbers.at(-1) ?? Number.MAX_VALUE))
                  }
                  onClick={() => {
                     const nextStep: number | null =
                        stepNumber === null
                           ? getNearestNextStep(currentTime, stepTimestamps)
                           : getNextTimestampedStep(stepNumber, validStepNumbers);
                     setStepNumber(nextStep);
                     scrollToStep(nextStep, playerHeight);
                     if (!loopCurrentStep) {
                        seekTo(stepTimestamps[nextStep].startTime);
                     }
                  }}
                  title="Next Step"
               >
                  <FontAwesomeIcon icon={faForwardStep} />
               </button>
            </div>
         </div>
      </div>
   );
}

/**
 * Returns the step number from a step id. If the step number isn't found, return null.
 */
function getStepNumberFromStepid(stepid: string) {
   const element = document.querySelector<HTMLElement>(`#s${stepid}`);
   const stepNumber = element?.dataset.stepNumber;
   return stepNumber ? Number.parseInt(stepNumber, 10) : null;
}

/**
 * Returns an object with the step number as the key and the timestamp as the
 * value. Differs from timestamps in that it uses the step number as the
 * key instead of the stepid.
 */
function stepNumberTimestamps(timestamps: [string, VideoTimestamp][]) {
   const stepNumberTimestamps: StepVideoTimestamp = {};
   timestamps.forEach(([stepid, timestamp]: [string, VideoTimestamp]) => {
      const stepNumber = getStepNumberFromStepid(stepid);
      if (stepNumber !== null) {
         stepNumberTimestamps[stepNumber] = timestamp;
      }
   });
   return stepNumberTimestamps;
}

/**
 * Scrolls to the step with the given step number, even if it doesn't have a
 * timestamp.
 */
function scrollToStep(stepNumber: number, playerHeight: number) {
   const metaElement = document.querySelector(
      `li.step[data-step-number="${CSS.escape(String(stepNumber))}"]`
   );
   if (metaElement) {
      if (isMobilePortrait()) {
         window.scrollTo({
            behavior: 'smooth',
            top: metaElement.getBoundingClientRect().top + window.pageYOffset - playerHeight,
         });
      } else {
         // Since the video player is now at the top, scroll to position the step at the top
         window.scrollTo({
            behavior: 'smooth',
            top:
               metaElement.getBoundingClientRect().top + window.pageYOffset - playerHeight / 2 - 20, // Add small margin
         });
      }
   }
}

/**
 * Tracks the timestamps of the video and updates the stepNumber.
 */
function trackTimestamps(
   getCurrentTime: () => Promise<number>,
   setStepNumber: Dispatch<SetStateAction<number | null>>,
   setCurrentTime: Dispatch<SetStateAction<number>>,
   timestamps: [string, VideoTimestamp][]
) {
   let lastStepNumber: number | null = null;
   return setInterval(async () => {
      const time = await getCurrentTime();
      setCurrentTime(time);
      let found = false;
      let currentStepNumber: number | null = null;

      timestamps.forEach(([stepid, timestamp]: [string, VideoTimestamp]) => {
         if (time >= timestamp.startTime && time < timestamp.endTime) {
            currentStepNumber = getStepNumberFromStepid(stepid);
            found = true;
         }
      });

      if (!found) {
         currentStepNumber = null;
      }

      // Only update if the step number has actually changed
      if (currentStepNumber !== lastStepNumber) {
         setStepNumber(currentStepNumber);
         lastStepNumber = currentStepNumber;
      }
   }, 500);
}

function getNextTimestampedStep(stepNumber: number, validStepNumbers: number[]) {
   const index = validStepNumbers.indexOf(stepNumber);
   if (index === -1 || index === validStepNumbers.length - 1) {
      return stepNumber;
   }
   return validStepNumbers[index + 1];
}

function getPreviousTimestampedStep(stepNumber: number, validStepNumbers: number[]) {
   const index = validStepNumbers.indexOf(stepNumber);
   if (index === -1 || index === 0) {
      return stepNumber;
   }
   return validStepNumbers[index - 1];
}

function getNearestPreviousStep(time: number, timestamps: StepVideoTimestamp) {
   let nearestStep = 1;
   let nearestTime = Number.MIN_VALUE;
   Object.entries(timestamps).forEach(([step, timestamp]) => {
      if (timestamp.endTime < time && timestamp.endTime > nearestTime) {
         nearestTime = timestamp.endTime;
         nearestStep = Number.parseInt(step);
      }
   });
   return nearestStep;
}

function getNearestNextStep(time: number, timestamps: StepVideoTimestamp) {
   let nearestStep = 1;
   let nearestTime = Number.MAX_VALUE;
   Object.entries(timestamps).forEach(([step, timestamp]) => {
      if (timestamp.startTime > time && timestamp.startTime < nearestTime) {
         nearestTime = timestamp.startTime;
         nearestStep = Number.parseInt(step);
      }
   });
   return nearestStep;
}

function isMobilePortrait() {
   return window.innerWidth < 768 && window.innerHeight > window.innerWidth;
}

function highlightCurrentPlayingStep(validStepNumbers: number[], stepNumber: number | null) {
   // Remove highlight from all currently highlighted steps
   const currentlyHighlighted = document.querySelectorAll('li.step.video-active-step');
   currentlyHighlighted.forEach(element => {
      element.classList.remove('video-active-step');
   });

   // Add highlight to the current step if it exists
   if (stepNumber !== null) {
      const element = document.querySelector(
         `li.step[data-step-number="${CSS.escape(String(stepNumber))}"]`
      );
      element?.classList.add('video-active-step');
   }
}

initializeComponent('VideoPlayerComponent', VideoPlayer);
